import { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CreditCard, Home, RotateCcw } from 'lucide-react'
import { usePermissions } from '../hooks/usePermissions'
import { useBillingConfig } from '../hooks/useBillingConfig'
import UnauthorizedPage from '../pages/UnauthorizedPage'
import { Button } from './ui/Button'
import { EmptyState } from './ui/EmptyState'
import { ErrorState } from './ui/ErrorState'
import { LoadingState } from './ui/LoadingState'

/**
 * LOT-UPGRADE — garde des routes /upgrade.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI CE COMPOSANT EXISTE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * L'audit visuel declarait /upgrade et /upgrade/success INATTEIGNABLES : la
 * garde fautive vivait DANS UpgradePage — `if (user.role !== 'USER')
 * navigate('/analytics')` — or aucun compte reel ne porte le role USER
 * (les comptes sont ADMIN ou AGENT), donc la redirection etait systematique.
 * Cette garde de page est supprimee ; l'aiguillage vit ici, une seule fois,
 * pour les trois routes du tunnel :
 *
 *   1. flag `billing_enabled` ETEINT (defaut) → « Offre non activee »,
 *      etat propre du kit (EmptyState), pour tous les roles ;
 *   2. flag ALLUME → tunnel atteignable par les roles USER **et** ADMIN.
 *      LOT-BILLING-RT (R1) : la garde « ADMIN uniquement » etait l'INVERSE de
 *      l'intention produit — le USER est l'acheteur cible (essai gratuit →
 *      offre Admin), et au test reel il recevait « Acces refuse ». Un ADMIN
 *      reste admis (regularisation d'un compte historique sans souscription) ;
 *      seul l'AGENT est exclu, comme au backend (403 sur create-checkout).
 *      Les etats fins (offre courante, CTA desactive si deja abonne) vivent
 *      dans UpgradePage ;
 *   3. config injoignable (backend en panne) → ErrorState avec relance,
 *      jamais un ecran blanc.
 */
export default function BillingRoute({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { isAdmin, isUser, isLoading: authLoading } = usePermissions()
  const { data: config, isLoading, isError, refetch } = useBillingConfig()

  // Meme convention que ProtectedRoute : pas de loader pendant la resolution
  // de l'authentification, pour eviter un clignotement au premier rendu.
  if (authLoading) {
    return null
  }

  if (isLoading) {
    return <LoadingState statCards={0} rows={4} />
  }

  if (isError || !config) {
    return (
      <ErrorState
        icon={CreditCard}
        tone="danger"
        title={t('billing.configError.title')}
        description={t('billing.configError.description')}
        actions={
          <>
            <Button variant="secondary" icon={Home} onClick={() => navigate('/analytics')}>
              {t('actions.backToDashboard')}
            </Button>
            <Button variant="primary" icon={RotateCcw} onClick={() => refetch()}>
              {t('actions.retry')}
            </Button>
          </>
        }
      />
    )
  }

  if (!config.billingEnabled) {
    return (
      /* DA-057 : cet état TIENT LIEU de page — la recette a mesuré qu'il
         n'avait aucun h1, donc aucun titre d'écran. `titleAs="h1"` le nomme. */
      <EmptyState
        icon={CreditCard}
        titleAs="h1"
        title={t('billing.disabled.title')}
        description={t('billing.disabled.description')}
        action={
          <Button variant="primary" icon={Home} onClick={() => navigate('/analytics')}>
            {t('actions.backToDashboard')}
          </Button>
        }
      />
    )
  }

  if (!isAdmin && !isUser) {
    return <UnauthorizedPage />
  }

  return <>{children}</>
}
